import PropTypes from 'prop-types';

import { ContainerProps as MuiContainerProps } from '@mui/material/Container';
import { Box, Link, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ArrowLeftIcon,
  FullLogo,
  MenuButton,
  MenuIcon,
  ShoppingIcon,
  User,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { useLocation, useNavigate } from 'react-router-dom';
import { AUTH_KEY, Button, clearAuthCookie, getCookieValue } from '@classity-edtech/ui-base';
import { useEffect, useState } from 'react';
import { useGetMyProfileQuery } from '../../services/users';
import { useLogoutMutation } from '../../services/authentication';
import { webPaths } from '../../routes/paths';
import { IconButton } from '@classity-edtech/ui-base/dist/components/Inputs';
import { useMyOrdersQuery } from '../../services/payments';
import { OrderFullfillmentStatus } from '@classity-edtech/platform-utils';

export interface HeaderProps extends MuiContainerProps {
  toggleOpenSidebar: () => void;
}

export const Header = ({ className, toggleOpenSidebar }: HeaderProps) => {
  const screens = useAppMediaQuery();
  const isSmallScreens = screens.isMobile || screens.isTablet;
  const { pathname } = useLocation();
  const smallScreens = screens.isMobile || screens.isTablet;
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [clientId, setClientId] = useState('');
  const { data: user } = useGetMyProfileQuery();
  const { data: myOrder } = useMyOrdersQuery({
    page: 1,
    limit: 1,
    status: OrderFullfillmentStatus.PLACED,
  });

  const [logout] = useLogoutMutation();

  const navigateHome = () => {
    navigate('/');
  };

  const toggleShowDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const handleLogout = async () => {
    const clientId = getCookieValue(AUTH_KEY);

    logout({
      clientId,
    });

    clearAuthCookie();

    navigate(`/${webPaths.signin}`);
  };

  useEffect(() => {
    setClientId(getCookieValue(AUTH_KEY));
  }, [user]);

  return (
    <Box
      sx={{
        transition: 'background 0.3s ease',
        background: 'transparent',
        backdropFilter: 'blur(10px)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        borderBottom: {
          xs: 'none',
          md: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          mx: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              py: { xs: 1, md: 2 },
              pr: { xs: 0, md: 3 },
              borderRight: {
                xs: 'none',
                md: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
              },
            }}
          >
            <FullLogo
              sx={{ width: { xs: '100px', md: '140px' }, height: 'auto', cursor: 'pointer' }}
              onClick={navigateHome}
            />
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2.5, ml: { xs: 0, md: 6 } }}>
            <Link
              href={`/${webPaths.curriculum}`}
              sx={{
                textDecoration: 'none',
                borderRadius: '8px',
                background:
                  pathname === `/${webPaths.curriculum}`
                    ? globalColours['$global-colour-background-neutral-active']
                    : '',
                '&:hover': {
                  background: globalColours['$global-colour-background-neutral-hover'],
                },
                width: 'fit-content',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography
                variant={smallScreens ? 'body3SemiBold' : 'body2SemiBold'}
                sx={{
                  color: globalColours['$global-colour-interactive-primary-default'],
                  p: 1,
                }}
              >
                K-12
              </Typography>

              <ArrowLeftIcon
                sx={{
                  width: '14px',
                  height: '14px',
                  color: globalColours['$global-colour-interactive-primary-default'],
                  transform: 'rotate(-90deg)',
                }}
              />
            </Link>
            <Link
              href={`#`}
              sx={{
                textDecoration: 'none',
                borderRadius: '8px',
                background:
                  pathname === `#` ? globalColours['$global-colour-background-neutral-active'] : '',
                '&:hover': {
                  background: globalColours['$global-colour-background-neutral-hover'],
                },
                width: 'fit-content',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography
                variant={smallScreens ? 'body3SemiBold' : 'body2SemiBold'}
                sx={{
                  color: globalColours['$global-colour-interactive-primary-default'],
                  p: 1,
                }}
              >
                Coaching
              </Typography>
            </Link>
            <Link
              href={`#`}
              sx={{
                textDecoration: 'none',
                borderRadius: '8px',
                background:
                  pathname === `#` ? globalColours['$global-colour-background-neutral-active'] : '',
                '&:hover': {
                  background: globalColours['$global-colour-background-neutral-hover'],
                },
                width: 'fit-content',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography
                variant={smallScreens ? 'body3SemiBold' : 'body2SemiBold'}
                sx={{
                  color: globalColours['$global-colour-interactive-primary-default'],
                  p: 1,
                }}
              >
                Masterclass
              </Typography>
            </Link>
            <Link
              href={`#`}
              sx={{
                textDecoration: 'none',
                borderRadius: '8px',
                background:
                  pathname === `#` ? globalColours['$global-colour-background-neutral-active'] : '',
                '&:hover': {
                  background: globalColours['$global-colour-background-neutral-hover'],
                },
                width: 'fit-content',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography
                variant={smallScreens ? 'body3SemiBold' : 'body2SemiBold'}
                sx={{
                  color: globalColours['$global-colour-interactive-primary-default'],
                  p: 1,
                }}
              >
                Classity Q
              </Typography>
            </Link>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0, md: 1 },
              py: { xs: 1, md: 2 },
              pl: { xs: 0, md: 3 },
              ml: 'auto',
              borderLeft: {
                xs: 'none',
                md: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
              },
            }}
          >
            {!user || !clientId ? (
              <>
                <Link href={`/${webPaths.teachSignin}`} style={{ textDecoration: 'none' }}>
                  <Box
                    sx={{
                      textDecoration: 'none',
                      borderRadius: '8px',
                      '&:hover': {
                        background: globalColours['$global-colour-background-neutral-hover'],
                      },
                      width: 'fit-content',
                    }}
                  >
                    <Typography
                      variant={smallScreens ? 'body3' : 'body2'}
                      sx={{
                        color: globalColours['$global-colour-interactive-primary-default'],
                        p: 1,
                      }}
                    >
                      Sign in
                    </Typography>
                  </Box>
                </Link>
                <Link href={`/${webPaths.teachSignup}`} style={{ textDecoration: 'none' }}>
                  <Box sx={{ textDecoration: 'none' }}>
                    <Button
                      variant='outlined'
                      sx={{
                        px: 1.5,
                        py: '5px',
                        '&:hover': {
                          background: globalColours['$global-colour-interactive-accent-default'],
                          color: globalColours['$global-colour-base-contrast'],
                        },
                        width: 'fit-content',
                      }}
                    >
                      <Typography variant={smallScreens ? 'body3SemiBold' : 'body2SemiBold'}>
                        Apply
                      </Typography>
                    </Button>
                  </Box>
                </Link>
              </>
            ) : (
              <>
                <MenuButton
                  {...({
                    icon: (
                      <ShoppingIcon
                        sx={{
                          width: '16px',
                          height: '16px',
                          color: globalColours['$global-colour-interactive-primary-default'],
                          '& .MuiBox-root': {
                            padding: 0,
                          },
                        }}
                      />
                    ),
                    size: 'small',
                    hideSideIcon: true,
                    hideAlert: !myOrder,
                  } as any)}
                />
                <Box sx={{ position: 'relative' }}>
                  <MenuButton
                    {...({
                      icon: (
                        <User
                          sx={{
                            width: '16px',
                            height: '16px',
                            color: globalColours['$global-colour-interactive-primary-default'],
                            '& .MuiBox-root': {
                              padding: 0,
                            },
                          }}
                        />
                      ),
                      size: 'small',
                      hideAlert: true,
                      hideSideIcon: isSmallScreens,
                      onClick: toggleShowDropdown,
                    } as any)}
                  />

                  {showDropdown && (
                    <>
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 'auto',
                          right: 0,
                          top: '100%',
                          background: globalColours['$global-colour-background-elevated'],
                          p: 1,
                          minWidth: '120px',
                          cursor: 'pointer',
                          boxShadow: '0px 0px 40px 0px rgba(69, 71, 69, 0.20)',
                        }}
                        onClick={handleLogout}
                      >
                        <Typography variant='body2SemiBold'>Logout</Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </>
            )}

            <IconButton
              onClick={toggleOpenSidebar}
              icon={
                <MenuIcon
                  sx={{
                    display: { xs: 'block', md: 'none' },
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer',
                  }}
                />
              }
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

Header.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

Header.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(Header)(({ theme }) => ``);
